import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Typography, Avatar, CircularProgress } from '@mui/material';
// import AccountTreeIcon from '@mui/icons-material/AccountTree';
import KnowledgeGraph from './components/KnowledgeGraph';
import GraphControls from './components/GraphControls';
import NodeDetails from './components/NodeDetails';
import ViewSwitcher from './components/ViewSwitcher';
import '../knowledge-graph/knowledge-graph.css';
import { defaultNodeCategories, fetchRoomKnowledgeGraph, fetchUserKnowledgeGraph } from 'src/utils/knowlege-graph-api';
import Iconify from 'src/components/iconify';
import { useSelector } from 'react-redux';
import '../knowledge-graph/main.css';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00f5ff',
    },
    secondary: {
      main: '#b24cff',
    },
    background: {
      default: '#030014',
      paper: '#0a0a1f',
    },
  },
  typography: {
    fontFamily: "'Outfit', -apple-system, sans-serif",
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: '#030014',
        },
      },
    },
  },
});

// Generate random stars for background
const generateStars = (count) => {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    animationDelay: `${Math.random() * 3}s`,
    size: Math.random() * 2 + 1,
  }));
};

function KnowledgeGraphPage() {
  const {user} = useSelector((state) => state.user);  
  const [selectedNode, setSelectedNode] = useState(null);
  const [graphConfig, setGraphConfig] = useState({
    nodeSize: 8,
    linkWidth: 1.5,
    particlesEnabled: true,
  });
  
  // View state
  const [activeView, setActiveView] = useState('user'); // 'user' or 'room'
  const [userId, setUserId] = useState('672a2c49e04c94e55c62f8e6'); // Default user ID
  const [roomId, setRoomId] = useState('687668378d88cd7b041ef394'); // Default room ID
  
  // Data state
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [nodeCategories, setNodeCategories] = useState(defaultNodeCategories);
  const [userProfile, setUserProfile] = useState(null);
  const [roomInfo, setRoomInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const stars = useMemo(() => generateStars(100), []);

  // Fetch data based on active view
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setSelectedNode(null);
    
    try {
      if (activeView === 'user' && userId) {
        const result = await fetchUserKnowledgeGraph(userId ,user);
        if (result.success) {
          setGraphData(result.graphData);
          setNodeCategories(result.nodeCategories);
          setUserProfile(result.user);
          setStats(result.stats);
          setRoomInfo(null);
        } else {
          setError(result.error);
          setGraphData({ nodes: [], links: [] });
        }
      } else if (activeView === 'room' && roomId) {
        const result = await fetchRoomKnowledgeGraph(roomId , user);
        if (result.success) {
          setGraphData(result.graphData);
          setNodeCategories(result.nodeCategories);
          setRoomInfo(result.room);
          setStats(result.stats);
          setUserProfile(null);
        } else {
          setError(result.error);
          setGraphData({ nodes: [], links: [] });
        }
      }
    } catch (err) {
      setError(err.message);
      setGraphData({ nodes: [], links: [] });
    }
    
    setIsLoading(false);
  }, [activeView, userId, roomId]);

  // Load data on mount and when view changes
  useEffect(() => {
    fetchData();
  }, []); // Only on mount

  const handleNodeClick = (node) => {
    setSelectedNode(node);
  };

  const handleConfigChange = (config) => {
    setGraphConfig(config);
  };

  const handleViewChange = (view) => {
    setActiveView(view);
    setSelectedNode(null);
  };

  // Get legend items from nodeCategories
  const legendItems = Object.entries(nodeCategories).map(([key, value]) => ({
    key,
    ...value,
  }));

  // Compute stats from graph data if not provided by API
  const displayStats = useMemo(() => {
    if (stats) return stats;
    
    const nodes = graphData.nodes || [];
    const links = graphData.links || [];
    
    return {
      nodes: nodes.length,
      connections: links.length,
      categories: Object.keys(nodeCategories).length,
    };
  }, [stats, graphData, nodeCategories]);

  // Get display info for header
  const displayInfo = useMemo(() => {
    if (activeView === 'user' && userProfile) {
      return {
        name: userProfile.name || 'User',
        role: userProfile.bio || userProfile.email || 'Knowledge Graph',
        avatar: userProfile.image,
        initials: userProfile.name ? userProfile.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U',
      };
    }
    if (activeView === 'room' && roomInfo) {
      return {
        name: roomInfo.name || 'Room',
        role: roomInfo.description || 'Room Knowledge Graph',
        avatar: roomInfo.image,
        initials: roomInfo.name ? roomInfo.name.charAt(0).toUpperCase() : 'R',
      };
    }
    return {
      name: activeView === 'user' ? 'User Graph' : 'Room Graph',
      role: 'Enter ID to load',
      initials: activeView === 'user' ? 'U' : 'R',
    };
  }, [activeView, userProfile, roomInfo]);

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box className="app-container">
        {/* Animated cosmic background */}
        <div className="cosmic-bg">
          <div className="stars">
            {stars.map((star) => (
              <div
                key={star.id}
                className="star"
                style={{
                  left: star.left,
                  top: star.top,
                  animationDelay: star.animationDelay,
                  width: `${star.size}px`,
                  height: `${star.size}px`,
                }}
              />
            ))}
          </div>
        </div>

        {/* Main content */}
        <div className="main-content">
          {/* Header */}
          <header className="header-bar">
            <div className="logo-section">
              <div className="logo-icon">
                {/* <AccountTreeIcon sx={{ fontSize: 22, color: 'white' }} /> */}

                <Iconify
                  icon="ic:baseline-account-tree"
                  width={20}
                  style={{ color: 'currentColor' }}
                />

              </div>
              <div>
                <div className="logo-text">Knowledge Graph</div>
                <div className="logo-subtitle">
                  {activeView === 'user' ? 'User Network' : 'Room Network'}
                </div>
              </div>
            </div>
            
            <div className="user-badge">
              <Avatar
                src={displayInfo.avatar}
                sx={{
                  width: 36,
                  height: 36,
                  background: activeView === 'user' 
                    ? 'linear-gradient(135deg, #ff3cac, #b24cff)'
                    : 'linear-gradient(135deg, #4d6cff, #00f5ff)',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                }}
              >
                {!displayInfo.avatar && displayInfo.initials}
              </Avatar>
              <div className="user-info">
                <span className="user-name">{displayInfo.name}</span>
                <span className="user-role">
                  {displayInfo.role.length > 30 
                    ? displayInfo.role.slice(0, 30) + '...' 
                    : displayInfo.role}
                </span>
              </div>
            </div>
          </header>

          {/* Graph */}
          <div className="graph-container">
            {isLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  gap: 2,
                }}
              >
                <CircularProgress 
                  sx={{ 
                    color: activeView === 'user' ? '#00f5ff' : '#4d6cff',
                  }} 
                />
                <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  Loading {activeView} graph...
                </Typography>
              </Box>
            ) : error ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  gap: 2,
                  color: '#ff6b6b',
                }}
              >
                <Typography variant="h6">Error loading graph</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', maxWidth: 400, textAlign: 'center' }}>
                  {error}
                </Typography>
              </Box>
            ) : (
              <KnowledgeGraph
                data={graphData}
                config={graphConfig}
                onNodeClick={handleNodeClick}
                nodeCategories={nodeCategories}
              />
            )}
          </div>

          {/* View Switcher Panel */}
          <div className="side-panel side-panel-left" style={{ top: 90 }}>
            <ViewSwitcher
              activeView={activeView}
              onViewChange={handleViewChange}
              userId={userId}
              roomId={roomId}
              onUserIdChange={setUserId}
              onRoomIdChange={setRoomId}
              onSubmit={fetchData}
              isLoading={isLoading}
            />
            
            {/* Graph Controls below View Switcher */}
            <Box sx={{ mt: 2 }}>
              <GraphControls 
                config={graphConfig} 
                onConfigChange={handleConfigChange} 
              />
            </Box>
          </div>

          {/* Node Details Panel */}
          {selectedNode && (
            <div className="side-panel side-panel-right">
              <NodeDetails 
                node={selectedNode} 
                onClose={() => setSelectedNode(null)}
                nodeCategories={nodeCategories}
              />
            </div>
          )}

          {/* Legend */}
          <div className="legend">
            <div className="legend-title">Categories</div>
            {legendItems.map((item) => (
              <div key={item.key} className="legend-item">
                <div 
                  className="legend-dot" 
                  style={{ backgroundColor: item.color, color: item.color }}
                />
                <span>{item.icon} {item.label}</span>
              </div>
            ))}
          </div>

          {/* Stats Bar */}
          <div className="stats-bar">
            <div className="stat-item">
              <div>
                <div className="stat-value">{displayStats.nodes || graphData.nodes?.length || 0}</div>
                <div className="stat-label">Nodes</div>
              </div>
            </div>
            <div className="stat-item">
              <div>
                <div className="stat-value">{displayStats.connections || graphData.links?.length || 0}</div>
                <div className="stat-label">Connections</div>
              </div>
            </div>
            <div className="stat-item">
              <div>
                <div className="stat-value">{displayStats.categories || Object.keys(nodeCategories).length}</div>
                <div className="stat-label">Categories</div>
              </div>
            </div>
            {displayStats.topics !== undefined && (
              <div className="stat-item">
                <div>
                  <div className="stat-value">{displayStats.topics}</div>
                  <div className="stat-label">Topics</div>
                </div>
              </div>
            )}
            {displayStats.members !== undefined && (
              <div className="stat-item">
                <div>
                  <div className="stat-value">{displayStats.members}</div>
                  <div className="stat-label">Members</div>
                </div>
              </div>
            )}
          </div>

          {/* Zoom hint */}
          <div className="zoom-controls">
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'rgba(255,255,255,0.4)', 
                textAlign: 'center',
                mb: 1,
                display: 'block',
                fontSize: '0.65rem',
              }}
            >
              Scroll to zoom • Drag to pan
            </Typography>
          </div>
        </div>
      </Box>
    </ThemeProvider>
  );
}

export default KnowledgeGraphPage;

